import { create } from 'zustand';
import { supabase } from '@/lib/supabase/client';
import type { User } from '@/types/auth';

interface AuthStore {
  user: User | null;
  isLoading: boolean;
  initialized: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

export const useAuthStore = create<AuthStore>((set) => ({
  user: null,
  isLoading: true,
  initialized: false,

  signIn: async (email: string, password: string) => {
    set({ isLoading: true });
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (authUser) {
        const { data: userData } = await supabase
          .from('users')
          .select('*')
          .eq('id', authUser.id)
          .single();
        
        if (userData) {
          set({ user: userData as User });
        }
      }
    } finally {
      set({ isLoading: false });
    }
  },

  signOut: async () => {
    await supabase.auth.signOut();
    set({ user: null });
  },

  refreshUser: async () => {
    set({ isLoading: true });
    try {
      const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();

      if (authError) {
        // Invalid or expired refresh token — sign out cleanly
        await supabase.auth.signOut();
        set({ user: null, initialized: true });
        return;
      }
      
      if (authUser) {
        const { data: userData } = await supabase
          .from('users')
          .select('*')
          .eq('id', authUser.id)
          .single();
        
        set({ user: userData as User || null, initialized: true });
      } else {
        set({ user: null, initialized: true });
      }
    } catch {
      await supabase.auth.signOut().catch(() => {});
      set({ user: null, initialized: true });
    } finally {
      set({ isLoading: false });
    }
  },
}));
